import type { Hit as AlgoliaHit } from "algoliasearch";
import type { Hit as InstantsearchHit } from "instantsearch.js";
import { useEffect, useMemo, useState } from "react";
import { useAlgoliaShopConfig } from "../providers/AlgoliaShopConfigProvider.tsx";

type EnrichHit<T> =
	T extends InstantsearchHit<AlgoliaAttributes>
		? InstantsearchHit<ItemboxAttributes>
		: T extends AlgoliaHit<AlgoliaAttributes>
			? AlgoliaHit<ItemboxAttributes>
			: never;
type CachedEnrichedProducts = EnrichHit<InstantsearchHit<AlgoliaAttributes> | AlgoliaHit<AlgoliaAttributes>>[];
const enrichedCache = new Map<string, CachedEnrichedProducts>();

export function useEnrichedProducts<T extends InstantsearchHit<AlgoliaAttributes> | AlgoliaHit<AlgoliaAttributes>>(items: T[]): EnrichHit<T>[] {
	const [enrichedProducts, setEnrichedProducts] = useState<EnrichHit<T>[]>(items as unknown as EnrichHit<T>[]);
	const {
		algoliaConfig: { siteCode, languageCode, isSearch },
	} = useAlgoliaShopConfig();
	const itemList = useMemo(() => {
		return items.map(({ itemNo, itemId }) => ({ itemNo, itemId }));
	}, [items]);

	const cacheKey = useMemo(() => {
		const itemIds = itemList
			.map((item) => item.itemId)
			//.sort()
			.join(",");
		return `${siteCode}:${languageCode}:${itemIds}`;
	}, [itemList, siteCode, languageCode]);
	useEffect(() => {
		if (!items?.length) {
			setEnrichedProducts([]);
			return;
		}
		const controller = new AbortController();

		if (enrichedCache.has(cacheKey)) {
			setEnrichedProducts(enrichedCache.get(cacheKey) as EnrichHit<T>[]);
			return;
		}
		const fetchEnrichedData = async () => {
			try {
				const response = await fetch(`/module/dcshop/GeneralAjaxData.php?function=getAlgoliaItemListRender&site=${siteCode}&language=${languageCode}`, {
					method: "POST",
					mode: "cors",
					cache: "no-cache",
					body: JSON.stringify({
						item_list: itemList,
						is_search: isSearch,
					}),
					headers: {
						"Content-type": "application/json; charset=UTF-8",
					},
					signal: controller.signal,
				});
				if (!response.ok) throw new Error("Failed to fetch enriched data");

				const data: Record<string, AsynchronousAttributes> = await response.json();
				const updatedProducts = items.map((p) => ({
					...p,
					...data[p.itemId],
				}));

				// Save to cache before updating state
				enrichedCache.set(cacheKey, updatedProducts);

				setEnrichedProducts(updatedProducts as unknown as EnrichHit<T>[]);
			} catch (error) {
				if (!(error instanceof DOMException && error.name === "AbortError")) {
					console.error("Enrichment failed:", error);
				}
			}
		};
		// Initially set items without enrichment to avoid flicker
		setEnrichedProducts(items as unknown as EnrichHit<T>[]);

		fetchEnrichedData().then();
		return () => {
			controller.abort();
		};
	}, [cacheKey]);

	return enrichedProducts;
}
